import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import {
  FileText,
  Pill, 
  Stethoscope, 
  Activity, 
  Sun, 
  Users, 
  TrendingUp, 
  AlertCircle,
  Calendar,
  HeartPulse,
  Baby,
  Utensils
} from 'lucide-react';
import { Client } from '@/types/client';
import { ClientMedicalCondition } from '@/types/client';
import { formatDisplayLabel } from '@/utils/display-labels';

// Keys to skip when rendering (internal/metadata or shown in header). Monitoring is shown from questionAnswers instead.
const SKIP_KEYS = new Set([
  'id', 'clientId', 'conditionId', 'conditionName', 'conditionSubtype', 'status', 'severity',
  'createdAt', 'addedAt', 'addedBy', 'updatedAt', 'questionAnswers', 'monitoring', 'source'
]);

// Configuration for known top-level sections
const SECTION_CONFIG: Record<string, { icon: React.ElementType; label: string }> = {
  treatment: { icon: Pill, label: 'Treatment and Medication' },
  diagnosisDetails: { icon: Stethoscope, label: 'Diagnosis Details' },
  symptoms: { icon: AlertCircle, label: 'Symptoms' },
  lifestyle: { icon: Sun, label: 'Lifestyle' },
  familyHistory: { icon: Users, label: 'Family History' },
  growth: { icon: TrendingUp, label: 'Growth and Development' },
  monitoring: { icon: Activity, label: 'Monitoring' },
  labStatus: { icon: FileText, label: 'Lab Status' },
  pregnancyDetails: { icon: Baby, label: 'Pregnancy Details' },
  pregnancyHistory: { icon: Baby, label: 'Pregnancy History' },
  menstrualHistory: { icon: Calendar, label: 'Menstrual History' },
  menstrual: { icon: Calendar, label: 'Menstrual' },
  fertility: { icon: Baby, label: 'Fertility' },
  comorbidities: { icon: HeartPulse, label: 'Comorbidities' },
  diet: { icon: Utensils, label: 'Diet History' },
  thyroidStatus: { icon: Activity, label: 'Thyroid Status' },
  safety: { icon: AlertCircle, label: 'Safety' },
  goals: { icon: TrendingUp, label: 'Goals' },
};

// Human-readable labels for known keys
const LABEL_MAP: Record<string, string> = {
  conditionName: 'Condition',
  conditionSubtype: 'Subtype',
  status: 'Status',
  severity: 'Severity',
  diagnosedDate: 'Diagnosed',
  notes: 'Notes',
  medication: 'Medication',
  medications: 'Medications',
  treatmentStatus: 'Treatment Status',
  insulinRegimen: 'Insulin Regimen',
  rapidInsulinType: 'Rapid Insulin',
  basalInsulinType: 'Basal Insulin',
  cgmType: 'CGM',
  carbCounting: 'Carb Counting',
  duration: 'Duration',
  cause: 'Cause',
  ageAtDiagnosis: 'Age at Diagnosis',
  identificationMethod: 'Identified By',
  reported: 'Reported Symptoms',
  physicalSigns: 'Physical Signs',
  weightImpact: 'Weight Impact',
  pregnancyPlanning: 'Pregnancy Planning',
  cycleImpact: 'Cycle Impact',
  menopausalStatus: 'Menopausal Status',
  bloodSugarImpact: 'Blood Sugar Impact',
  hypoFrequency: 'Hypo Frequency',
  severeHypos: 'Severe Hypos',
  diabulimia: 'Diabulimia',
  bmiPercentile: 'BMI Percentile',
  physicalActivity: 'Physical Activity',
  dietManagement: 'Diet Management',
  general: 'Family History',
  previousInterventions: 'Previous Interventions',
  nutritionSupportGoals: 'Nutrition Goals',
  managementPriorities: 'Management Priorities',
};

function formatLabel(key: string): string {
  if (LABEL_MAP[key]) return LABEL_MAP[key];
  return formatDisplayLabel(key.replace(/([A-Z])/g, ' $1'));
}

function getSymptomsDisplay(val: unknown): string | null {
  if (!val) return null;
  
  // If it's already an array of strings/objects (legacy or different format)
  if (Array.isArray(val)) {
    const names = val.map(item => {
      if (typeof item === 'string') return item;
      if (typeof item === 'object' && (item as any)?.symptomName) return (item as any).symptomName;
      return null;
    }).filter(Boolean);
    if (names.length === 0) return null;
    return Array.from(new Set(names)).join(', ');
  }

  // If it's the object format (reported, digestive, etc)
  if (typeof val === 'object') {
    const allSymptoms = new Set<string>();
    Object.values(val as Record<string, unknown>).forEach(v => {
      if (Array.isArray(v)) {
        v.forEach(item => {
           if (typeof item === 'string') allSymptoms.add(item);
           else if (typeof item === 'object' && (item as any)?.symptomName) allSymptoms.add((item as any).symptomName);
        });
      } else if (typeof v === 'string') {
        allSymptoms.add(v);
      }
    });
    
    if (allSymptoms.size === 0) return null;
    return Array.from(allSymptoms).join(', ');
  }
  
  return null;
}

/** Returns whether the condition has any content to show when expanded (fields or questionAnswers). */
function conditionHasExpandableContent(condition: ClientMedicalCondition): boolean {
  if (condition.questionAnswers?.length) return true;
  const data = condition as unknown as Record<string, unknown>;
  return dataHasDisplayableContent(data);
}

function dataHasDisplayableContent(data: Record<string, unknown>): boolean {
  const entries = Object.entries(data).filter(
    ([k, v]) => !SKIP_KEYS.has(k) && v != null && v !== '' && (Array.isArray(v) ? v.length > 0 : true)
  );
  if (entries.length === 0) return false;
  for (const [key, val] of entries) {
    if (key === 'symptoms') {
      if (getSymptomsDisplay(val)) return true;
      continue;
    }
    if (typeof val !== 'object' || val === null || Array.isArray(val) || val instanceof Date) {
      if (val != null && val !== '' && (Array.isArray(val) ? (val as unknown[]).length > 0 : true)) return true;
    } else {
      if (dataHasDisplayableContent(val as Record<string, unknown>)) return true;
    }
  }
  return false;
}

function formatValue(val: unknown): React.ReactNode {
  if (val == null || val === '') return null;
  if (typeof val === 'boolean') return val ? 'Yes' : 'No';
  if (Array.isArray(val)) {
    if (val.length === 0) return null;
    return (
      <div className="flex flex-wrap gap-1">
        {val.map((item, i) => (
          <Badge key={i} variant="secondary" className="font-normal text-xs">
            {String(item)}
          </Badge>
        ))}
      </div>
    );
  }
  if (typeof val === 'object') {
    return null; // Handled as nested section
  }
  return String(val);
}

function ConditionFieldList({
  data,
  prefix = '',
  depth = 0,
}: {
  data: Record<string, unknown>;
  prefix?: string;
  depth?: number;
}) {
  const entries = Object.entries(data).filter(
    ([k, v]) => !SKIP_KEYS.has(k) && v != null && v !== '' && (Array.isArray(v) ? v.length > 0 : true)
  );

  if (entries.length === 0) return null;

  // Separate fields (primitives/arrays) and sections (nested objects)
  // Treat 'symptoms' as a field to flatten it
  const fields = entries.filter(([key, val]) => {
    if (key === 'symptoms') return true;
    return typeof val !== 'object' || val === null || Array.isArray(val) || val instanceof Date;
  });

  const sections = entries.filter(([key, val]) => {
    if (key === 'symptoms') return false;
    return typeof val === 'object' && val !== null && !Array.isArray(val) && !(val instanceof Date);
  });

  return (
    <div className="space-y-4">
      {/* Primitive Fields in Grid */}
      {fields.length > 0 && (
        <div className={`grid grid-cols-1 ${depth === 0 ? 'md:grid-cols-2 gap-x-6 gap-y-3' : 'gap-2'}`}>
          {fields.map(([key, val]) => {
            let formatted: React.ReactNode;
            
            if (key === 'symptoms') {
              formatted = getSymptomsDisplay(val);
            } else {
              formatted = formatValue(val);
            }
            
            if (formatted == null) return null;
            
            return (
              <div key={prefix + key} className="flex flex-col gap-1">
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  {formatLabel(key)}
                </span>
                <span className="text-sm text-foreground font-medium">{formatted}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Nested Sections */}
      {sections.length > 0 && (
        <div className="space-y-4">
          {sections.map(([key, val]) => {
            const nested = val as Record<string, unknown>;
            const config = SECTION_CONFIG[key];
            const Icon = config?.icon;
            const label = config?.label || formatLabel(key);

            return (
              <div key={prefix + key} className={`rounded-lg ${depth === 0 ? 'bg-muted/30 p-4 border border-border/50' : 'pl-4 border-l-2 border-muted'}`}>
                <div className="flex items-center gap-2 mb-3">
                  {Icon && <Icon className="h-4 w-4 text-primary" />}
                  <h4 className="text-sm font-semibold text-foreground">{label}</h4>
                </div>
                <ConditionFieldList data={nested} prefix={prefix + key + '.'} depth={depth + 1} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function QuestionAnswersBlock({ answers }: { answers: ClientMedicalCondition['questionAnswers'] }) {
  if (!answers?.length) return null;
  return (
    <div className="rounded-lg bg-muted/30 p-4 border border-border/50">
      <div className="flex items-center gap-2 mb-3">
        <Activity className="h-4 w-4 text-primary" />
        <h4 className="text-sm font-semibold text-foreground">Monitoring</h4>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
        {answers.map((qa, i) => {
          const display =
            qa.unit != null && qa.value != null && qa.value !== ''
              ? `${String(qa.value)} ${qa.unit}`
              : qa.value != null && qa.value !== ''
                ? String(qa.value)
                : null;
          if (display == null) return null;
          return (
            <div key={qa.questionId + String(i)} className="flex flex-col gap-1">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                {qa.questionText}
              </span>
              <span className="text-sm text-foreground font-medium">{display}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ConditionHeader({ condition }: { condition: ClientMedicalCondition }) {
  const name = formatDisplayLabel(condition.conditionName || condition.conditionId || '');
  return (
    <div className="flex flex-1 flex-col sm:flex-row sm:items-center justify-between gap-2 text-left">
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <span className="text-lg font-semibold text-primary">{name}</span>
          {condition.conditionSubtype && (
            <span className="text-sm font-medium text-muted-foreground">
              — {condition.conditionSubtype}
            </span>
          )}
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <Badge
          variant={condition.status === 'active' || condition.status === 'managed' ? 'default' : 'secondary'}
          className="h-6"
        >
          {condition.status === 'active' || condition.status === 'managed' ? 'Active' : 'Inactive'}
        </Badge>
      </div>
    </div>
  );
}

function MedicalConditionItem({
  condition,
  hasContent,
}: {
  condition: ClientMedicalCondition;
  hasContent: boolean;
}) {
  const data = condition as unknown as Record<string, unknown>;

  if (!hasContent) {
    return (
      <div className="border border-border/50 rounded-lg bg-card px-4 py-4">
        <ConditionHeader condition={condition} />
      </div>
    );
  }

  return (
    <AccordionItem value={condition.id} className="border border-border/50 rounded-lg bg-card px-4">
      <AccordionTrigger className="hover:no-underline py-4 mr-4">
        <ConditionHeader condition={condition} />
      </AccordionTrigger>
      <AccordionContent className="pt-2 pb-6 space-y-4">
        <ConditionFieldList data={data} />
        <QuestionAnswersBlock answers={condition.questionAnswers} />
      </AccordionContent>
    </AccordionItem>
  );
}

interface MedicalConditionsSectionProps {
  client: Client;
}

export function MedicalConditionsSection({ client }: MedicalConditionsSectionProps) {
  const conditions = client?.medicalConditionDetails;

  if (!conditions || conditions.length === 0) {
    return (
      <Card className="shadow-lg hover:shadow-xl transition-all duration-300 border-0 bg-gradient-to-br from-background to-purple/5">
        <CardHeader className="pb-4">
          <CardTitle className="text-xl font-bold text-foreground flex items-center">
            <div className="h-10 w-10 bg-purple/10 rounded-full flex items-center justify-center mr-3">
              <FileText className="h-5 w-5 text-purple" />
            </div>
            Medical History
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="text-center py-12 bg-gradient-to-br from-muted/30 to-muted/10 rounded-xl border border-border/30">
            <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-base text-muted-foreground">No medical conditions recorded.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const conditionsWithContent = conditions.filter((c) => conditionHasExpandableContent(c));
  const conditionsWithoutContent = conditions.filter((c) => !conditionHasExpandableContent(c));
  const hasAnyExpandable = conditionsWithContent.length > 0;
  const defaultValue =
    conditionsWithContent.length === 1 ? conditionsWithContent[0].id : undefined;

  return (
    <Card className="shadow-lg hover:shadow-xl transition-all duration-300 border-0 bg-gradient-to-br from-background to-purple/5">
      <CardHeader className="pb-4">
        <CardTitle className="text-xl font-bold text-foreground flex items-center">
          <div className="h-10 w-10 bg-purple/10 rounded-full flex items-center justify-center mr-3">
            <FileText className="h-5 w-5 text-purple" />
          </div>
          Medical History
        </CardTitle>
        <CardDescription className="text-base text-muted-foreground">
          Client&apos;s medical conditions, medications, and treatment details
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {conditionsWithoutContent.map((condition) => (
          <MedicalConditionItem
            key={condition.id}
            condition={condition}
            hasContent={false}
          />
        ))}
        {hasAnyExpandable ? (
          <Accordion type="single" collapsible defaultValue={defaultValue} className="space-y-4">
            {conditionsWithContent.map((condition) => (
              <MedicalConditionItem
                key={condition.id}
                condition={condition}
                hasContent={true}
              />
            ))}
          </Accordion>
        ) : null}
      </CardContent>
    </Card>
  );
}
